import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useState as useStateRR } from "react";

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [affs, setAffs] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/campaigns").then((r) => setCampaigns(r.data));
  useEffect(() => {
    load();
    api.get("/affiliates").then((r) => setAffs(r.data));
  }, []);

  const openDetail = async (id) => {
    const { data } = await api.get(`/campaigns/${id}`);
    setDetail(data);
  };

  const affName = (id) => affs.find((a) => a.id === id)?.name || id;

  return (
    <div>
      <PageHeader
        title="Campaigns"
        subtitle={`${campaigns.length} campaigns`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-10" data-testid="new-campaign-btn"><Plus className="h-4 w-4 mr-1.5" /> New Campaign</Button>
            </DialogTrigger>
            <NewCampaignDialog affs={affs} onCreated={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />
      <div className="p-6 md:p-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((c) => (
            <div key={c.id} data-testid={`campaign-${c.id}`} className="rounded-3xl border border-border/60 bg-card overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => openDetail(c.id)}>
              {c.cover_url && <img src={c.cover_url} alt="" className="h-40 w-full object-cover" />}
              <div className="p-5">
                <h3 className="text-lg font-semibold tracking-tight">{c.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.brief}</p>
                <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
                  <span>{c.start_date} → {c.end_date}</span>
                  <span>{c.assigned_affiliate_ids.length} affiliates</span>
                </div>
              </div>
            </div>
          ))}
          {campaigns.length === 0 && <div className="col-span-3 text-sm text-muted-foreground p-8">No campaigns yet.</div>}
        </div>

        {detail && (
          <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
            <DialogContent className="rounded-3xl max-w-2xl">
              <DialogHeader>
                <DialogTitle>{detail.campaign.title}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">{detail.campaign.brief}</p>
              <div className="text-xs text-muted-foreground">{detail.campaign.start_date} → {detail.campaign.end_date}</div>
              <div className="space-y-2 mt-2 max-h-96 overflow-y-auto">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Submissions</div>
                {detail.submissions.map((s) => (
                  <SubmissionRow key={s.id} sub={s} affName={affName(s.affiliate_id)} onUpdated={() => openDetail(detail.campaign.id)} />
                ))}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}

function SubmissionRow({ sub, affName, onUpdated }) {
  const [link, setLink] = useStateRR(sub.link || "");
  const [status, setStatus] = useStateRR(sub.status);
  const save = async () => {
    await api.patch(`/submissions/${sub.id}`, null, { params: { link, status } });
    toast.success("Updated");
    onUpdated();
  };
  return (
    <div className="rounded-xl border border-border/60 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold">{affName}</div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-full border border-border bg-card px-3 text-xs">
          <option value="pending">Pending</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
      <div className="flex gap-2">
        <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Content link" className="rounded-xl h-9" />
        <Button size="sm" onClick={save} className="rounded-full h-9">Save</Button>
      </div>
    </div>
  );
}

function NewCampaignDialog({ affs, onCreated }) {
  const [form, setForm] = useState({
    title: "", brief: "", cover_url: "", start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0, 10),
    assigned_affiliate_ids: [],
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleAff = (id) => setForm((f) => ({ ...f, assigned_affiliate_ids: f.assigned_affiliate_ids.includes(id) ? f.assigned_affiliate_ids.filter((x) => x !== id) : [...f.assigned_affiliate_ids, id] }));
  const submit = async () => {
    try {
      await api.post("/campaigns", form);
      toast.success("Campaign created");
      onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <DialogContent className="rounded-3xl max-w-lg">
      <DialogHeader><DialogTitle>New Campaign</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Title</Label><Input data-testid="camp-title" value={form.title} onChange={set("title")} className="mt-1 rounded-xl" /></div>
        <div><Label>Brief</Label><Textarea value={form.brief} onChange={set("brief")} className="mt-1 rounded-xl" rows={3} /></div>
        <div><Label>Cover URL (optional)</Label><Input value={form.cover_url} onChange={set("cover_url")} className="mt-1 rounded-xl" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={set("start_date")} className="mt-1 rounded-xl" /></div>
          <div><Label>End</Label><Input type="date" value={form.end_date} onChange={set("end_date")} className="mt-1 rounded-xl" /></div>
        </div>
        <div>
          <Label>Assign Affiliates</Label>
          <div className="max-h-40 overflow-y-auto mt-1 space-y-1 border border-border rounded-xl p-2">
            {affs.map((a) => (
              <label key={a.id} className="flex items-center gap-2 text-sm px-2 py-1 hover:bg-secondary rounded">
                <input type="checkbox" checked={form.assigned_affiliate_ids.includes(a.id)} onChange={() => toggleAff(a.id)} />
                {a.name}
              </label>
            ))}
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="submit-camp" onClick={submit} className="rounded-full">Create</Button>
      </DialogFooter>
    </DialogContent>
  );
}
